import React, { useState, useEffect } from "react";
import style from "../CommonStyles/commonformstyles.module.scss";
import { InputLabel, MenuItem, FormControl, Select } from "@mui/material";
import { FaCloudArrowUp } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useMultipleFileUploadAws from "../../../../hooks/AWSFileUpload/useMultipleFileUploadAws";
import uploadAnimation from "../../../../assets/gif/uploadAnimation.gif";
import LinearProgressBar from "../../../../components/ProgressBar/LinearProgressBar";
import {
  useAddEquipment,
  useFetchAllEquipmentTypes,
} from "../../../../hooks/partnerHook/usePartnerEquipment";
import { useDispatch, useSelector } from "react-redux";
import { setEquipmentType } from "../../../../store/Partner/siteData";

const EquipmentsForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {
    uploadErrors,
    uploadFileToS3,
    cancelUpload,
    mediaUrls,
    uploadProgresses,
    estimatedTimes,
  } = useMultipleFileUploadAws();

  const userData = useSelector((state) => state.partner.user);
  const partnerId = userData?.partner?.id;

  const [formData, setFormData] = useState({
    title: "",
    equipment_model: "",
    description: "",
    equipment_category: "",
    equipment_type: {},
    image: null,
    droneMediaUrl: null,
    media_type: "",
  });

  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(uploadAnimation);
  const [uniqueCategories, setUniqueCategories] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { data: equipmentTypeData } = useFetchAllEquipmentTypes();
  const equipmentTypes = useSelector((state) => state.siteData.equipmenttype);

  useEffect(() => {
    if (equipmentTypeData) {
      dispatch(setEquipmentType(equipmentTypeData));
    }
  }, [equipmentTypeData, dispatch]);

  useEffect(() => {
    if (equipmentTypes && equipmentTypes.length > 0) {
      const unique = [
        ...new Set(equipmentTypes.map((type) => type.equipment_category)),
      ];
      setUniqueCategories(unique);
    }
  }, [equipmentTypes]);

  useEffect(() => {
    if (mediaUrls && formData?.image?.name && mediaUrls[formData.image.name]) {
      setFormData((prevData) => ({
        ...prevData,
        droneMediaUrl: mediaUrls[formData.image.name],
      }));
      setUploadSuccess(true);
      setUploading(false);
      setUploadPreview(formData.droneMediaUrl);
    }
  }, [mediaUrls, formData.image?.name, formData.droneMediaUrl]);

  const { mutate: addEquipment, isPending: equipmentAddPending } =
    useAddEquipment(navigate);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (!formData.droneMediaUrl) {
        setErrorMessage("Please upload an image before submitting.");
        return;
      }

      const { image, ...equipmentData } = formData;
      console.log("Formatted Equipment Data:", equipmentData);

      await addEquipment(
        { partnerId, equipment: equipmentData },
        {
          onSuccess: async () => {
            console.log("Equipment added successfully");
            await queryClient.invalidateQueries(["equipments", partnerId]);
            navigate("/dashboard/equipments");
          },
          onError: (error) => {
            console.error("Error adding equipment:", error);
            setErrorMessage("Failed to add equipment. Please try again.");
          },
        }
      );
    } catch (error) {
      console.error("Error submitting form!", error);
      setErrorMessage("Failed to add equipment. Please try again.");
    }
  };

  const handleInputChange = (event) => {
    const { name, value, files } = event.target;

    if (name === "image" && files) {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const isImage = file.type.startsWith("image/");
          const isVideo = file.type.startsWith("video/");

          if (isImage || isVideo) {
            setFormData((prevData) => ({
              ...prevData,
              image: file,
              media_type: isImage ? "image" : "video",
            }));
          } else {
            setErrorMessage("Please upload a valid image or video file.");
          }
        };
        reader.readAsDataURL(file);
        setUploadSuccess(false);
        setUploadPreview(uploadAnimation);
      }
    } else if (name === "equipment_type") {
      const selectedType = equipmentTypes.find(
        (type) => type.equipment_type === value.equipment_type
      );
      setFormData((prevData) => ({
        ...prevData,
        equipment_type: selectedType || value,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    const validFormats = ["image/png", "image/jpeg"];
    if (!validFormats.includes(file.type)) {
      setErrorMessage("Invalid file format. Only PNG & JPG are allowed.");
      return;
    }

    setErrorMessage("");
    setFormData((prevData) => ({
      ...prevData,
      media_type: "image",
      image: file,
    }));

    const reader = new FileReader();
    reader.onload = () => {
      setUploadPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleBrowseClick = () => {
    document.getElementById("file-input").click();
  };

  const handleCancel = () => {
    if (formData.image) {
      cancelUpload(formData.image.name);
    }
    setFormData((prevData) => ({
      ...prevData,
      image: null,
      droneMediaUrl: "",
    }));
    setUploading(false);
    setUploadSuccess(false);
    setErrorMessage("");
  };

  const handleUpload = async () => {
    if (formData.image) {
      setUploading(true);
      try {
        await uploadFileToS3(formData.image);
      } catch (error) {
        setUploading(false);
        setErrorMessage("Failed to upload the file. Please try again.");
      }
    }
  };

  if (equipmentAddPending) {
    return (
      <div className="container-background content-center">
        <div
          style={{
            background: "white",
            borderRadius: "1rem",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={style.dashboardFormContainerWrapper}>
      <div className={style.dashboardFormContainer}>
        <form className={style.mainFormContainer} onSubmit={handleSubmit}>
          <h2 className={style.dashboardFormContinerHeading}>
            Add Your Equipment
          </h2>
          <p className={style.noEquipmentDescriptiond}>
            Provide details about your equipment and its capabilities.
          </p>
          <div className={style.dashboardFormInputContainer}>
            <h3 className={style.labels}>Equipment Name</h3>
            <input
              className={style.dashFormContainerInput}
              name="title"
              type="text"
              value={formData.title}
              placeholder="Enter Equipment Name"
              onChange={handleInputChange}
              required
            />
            <h3 className={style.labels}>Equipment Model</h3>
            <input
              className={style.dashFormContainerInput}
              name="equipment_model"
              type="text"
              value={formData.equipment_model}
              placeholder="Enter Equipment Model"
              onChange={handleInputChange}
            />
            <h3 className={style.labels}>Equipment Description</h3>
            <input
              className={style.dashFormContainerInput}
              name="description"
              type="text"
              value={formData.description}
              placeholder="Enter Equipment Description"
              onChange={handleInputChange}
              required
            />
            <h3 className={style.labels}>Upload Image</h3>
            <div
              className={style.DragUploader}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <div
                className={`${style.DragUploaderImageContainer} ${
                  dragging ? style.dragActive : ""
                }`}
              >
                {!formData.image ? (
                  <>
                    <FaCloudArrowUp className={style.uploadIcon} />
                    <p className={style.uploadText}>Drop images here</p>
                    <p className={style.supportedFormats}>
                      Supported formats: PNG, JPG
                    </p>
                    <p className={style.orText}>OR</p>
                    <h2
                      className={style.browseText}
                      onClick={handleBrowseClick}
                    >
                      <strong>Browse Images</strong>
                    </h2>
                  </>
                ) : (
                  <img
                    src={uploadPreview}
                    alt="Preview"
                    className={style.imagePreview}
                  />
                )}

                <input
                  id="file-input"
                  type="file"
                  accept="image/png, image/jpeg"
                  className={style.fileInput}
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  hidden
                />
              </div>

              <div className={style.buttonContainer}>
                <span className={style.cancelText} onClick={handleCancel}>
                  Cancel
                </span>
                <button
                  type="button"
                  className={style.uploadButton}
                  onClick={handleUpload}
                  disabled={uploadSuccess}
                >
                  {uploadSuccess ? "Uploaded" : "Upload"}
                </button>
              </div>

              {errorMessage && (
                <p className={style.errorMessage}>{errorMessage}</p>
              )}
            </div>

            <div className={style.categorySection}>
              <div className={style.selectContainer}>
                <h3 className={style.labels1}>Choose Category</h3>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <InputLabel id="category-select-label">Category</InputLabel>
                  <Select
                    labelId="category-select-label"
                    id="category-select"
                    value={formData.equipment_category}
                    label="Category"
                    onChange={handleInputChange}
                    name="equipment_category"
                    required
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {uniqueCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className={style.selectContainer}>
                <h3 className={style.labels1}>Choose Type</h3>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <InputLabel id="type-select-label">Select Type</InputLabel>
                  <Select
                    labelId="type-select-label"
                    id="type-select"
                    value={formData.equipment_type?.equipment_type || ""}
                    label="Equipment Type"
                    onChange={(e) => {
                      const selectedType = equipmentTypes.find(
                        (type) => type.equipment_type === e.target.value
                      );
                      setFormData((prevData) => ({
                        ...prevData,
                        equipment_type: selectedType || {},
                      }));
                    }}
                    name="equipment_type"
                    required
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {equipmentTypes.map((type, index) =>
                      formData.equipment_category ===
                      type.equipment_category ? (
                        <MenuItem key={index} value={type.equipment_type}>
                          {type.equipment_type}
                        </MenuItem>
                      ) : null
                    )}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          <div className={style.formButtonsContainer}>
            <button className={style.dashboardFormSaveButtonDown} type="submit">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EquipmentsForm;
